// Card 10, ship tracks (R548, ui-map-interface.md §17.3.10).
//
// A log plots a passage as a dotted track with a tick and a time at every
// hour. This is the same plot behind every own hull: the last 27000 MAP
// seconds as dots, a tick with the world clock every 5400 (R588, R1453),
// fading to nothing at the tail.
//
// **Own hulls only.** It keeps the rule the wake keeps (§5): a track behind
// a contact is a course the server withheld, and the client must never
// invent one.
//
// **The piece owns its ring buffer.** The fleet layer holds nine wake
// points at 0.12 s, which is two seconds of scar and no clock at all. A
// window of map time at one point per six px of travel is a different ledger, so
// this piece keeps its own and sweeps a fleet the layer no longer reports.

import type { CHART } from '../../../gen/presentation';
import type { FrameInfo } from '../../layer';
import { hhmm } from '../../../locale/clock';
import { CHART_INK, faceOf, offCull } from './shared';
import {
  type ChartContext, type ChartFleet, type ChartSinks, type Piece,
} from '../context';

/** One plotted point: where the hull was, and the world time it was there. */
interface Point {
  x: number;
  y: number;
  /** The world time of day the point went down at, as `hh:mm`, written
   *  once for a tick, so a frame builds no string (§10.3); '' for a point
   *  with no tick. */
  at: string;
  /** Map seconds since the track started, so the window can drop it. */
  mapS: number;
  /** The heading the hull ran at, for a tick's own rotation. */
  rot: number;
  /** Does this point carry a tick and a time? */
  tick: boolean;
}

/** One hull's plot. */
interface Track {
  pts: Point[];
  /** Map seconds since this track started. It never wraps. */
  mapS: number;
  /** Map seconds of travel since the last tick went down. */
  sinceTick: number;
  lastX: number;
  lastY: number;
  /** Has the track a first point yet? */
  started: boolean;
  /** The frame the hull was last reported on, so a lost hull is swept. */
  seen: number;
}

export class ShipTracksPiece implements Piece {
  readonly id = 'ship_tracks' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  /** A live piece packs every frame; it repacks for nothing else. */
  readonly rebuildOn = [] as const;

  private readonly tracks = new Map<number, Track>();
  /** The frame counter the sweep compares against. */
  private stamp = 0;

  rebuild(_ctx: ChartContext): void { /* the ledger is built by `update` */ }

  /**
   * Advances every own hull's plot by this frame's travel.
   *
   * The clock is the MAP clock (R1453): a point goes down every `dot_px`
   * of travel whatever the frame rate, and the window and the tick cadence
   * count map seconds, the clock the hull moves on. The tick still writes
   * the world clock.
   */
  update(ctx: ChartContext, frame: FrameInfo): void {
    const fleets = ctx.scene.fleets;
    if (!fleets) return;
    const t = ctx.cfg.pieces.ship_tracks;
    const scale = ctx.state.tacticalScale || 1;
    const dMap = (frame.dtMs / 1000) * scale;
    this.stamp++;
    for (const f of fleets.chartFleets()) {
      if (!f.own) continue;
      this.advance(f, dMap, ctx.worldClockS, t.fig);
    }
    // Sweep the hulls that are gone: a track with no hull is a course
    // nobody is running.
    for (const [id, tr] of this.tracks) {
      if (tr.seen !== this.stamp) this.tracks.delete(id);
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.ship_tracks;
    const r = t[ctx.reading];
    const fig = t.fig;
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const ink = CHART_INK.pale;
    const span = fig.alpha_head - fig.alpha_tail;
    const step = t.text.main;
    const cull = ctx.cull;
    for (const tr of this.tracks.values()) {
      const n = tr.pts.length;
      for (let i = 0; i < n; i++) {
        const p = tr.pts[i];
        if (offCull(cull, p.x, p.y)) continue;
        // The fade runs from the tail of the plot to the hull.
        const f = n < 2 ? 1 : i / (n - 1);
        sinks.circles.disc(p.x, p.y, fig.dot_r, ink, fig.alpha_tail + span * f);
        if (!p.tick) continue;
        const a = fig.tick_alpha_tail + span * f;
        const cos = Math.cos(p.rot), sin = Math.sin(p.rot);
        if (hist) {
          const c = t.historical.fig.cross_r;
          this.cross(sinks, p, cos, sin, c, ink, a, r.width);
        } else {
          const k = fig.tick_len;
          // A tick lies ACROSS the track, so it is the travel normal.
          sinks.segments.line(
            p.x + sin * k, p.y - cos * k, p.x - sin * k, p.y + cos * k,
            r.width, ink, a,
          );
        }
        const lift = fig.label_lift;
        ctx.glyphs.word(
          sinks.quads, p.at,
          p.x + sin * lift, p.y - cos * lift,
          face, step, ink, fig.label_alpha_tail + span * f,
          { anchor: 'centre' },
        );
      }
    }
  }

  dispose(): void { this.tracks.clear(); }

  // -- the private half ------------------------------------------------------

  /** The historical tick: a dead-reckoning cross, turned to the travel. */
  private cross(
    s: ChartSinks, p: Point, cos: number, sin: number,
    c: number, ink: number, a: number, w: number,
  ): void {
    // The two arms of the mockup's cross, rotated into the travel frame:
    // (-c, -c) to (c, c), then (-c, c) to (c, -c).
    const cc = c * cos, cs = c * sin;
    s.segments.line(p.x - cc + cs, p.y - cs - cc, p.x + cc - cs, p.y + cs + cc, w, ink, a);
    s.segments.line(p.x - cc - cs, p.y - cs + cc, p.x + cc + cs, p.y + cs - cc, w, ink, a);
  }

  /** One hull's own advance: the travel, the window and the tick cadence. */
  private advance(
    f: ChartFleet, dMap: number, clockS: number,
    fig: typeof CHART.pieces.ship_tracks.fig,
  ): void {
    let tr = this.tracks.get(f.id);
    if (!tr) {
      tr = {
        pts: [], mapS: 0, sinceTick: 0,
        lastX: f.x, lastY: f.y, started: false, seen: this.stamp,
      };
      this.tracks.set(f.id, tr);
    }
    tr.seen = this.stamp;
    tr.mapS += dMap;
    tr.sinceTick += dMap;
    const dx = f.x - tr.lastX, dy = f.y - tr.lastY;
    const moved = Math.hypot(dx, dy);
    if (!tr.started || moved >= fig.dot_px) {
      const tick = !tr.started || tr.sinceTick >= fig.tick_every_map_sec;
      if (tick) tr.sinceTick = 0;
      tr.pts.push({
        x: f.x, y: f.y, at: tick ? hhmm(clockS) : '', mapS: tr.mapS,
        rot: moved > 0 ? Math.atan2(dy, dx) : f.heading, tick,
      });
      tr.lastX = f.x; tr.lastY = f.y;
      tr.started = true;
    }
    // The window, then the ceiling. The window is the rule; the ceiling is
    // a guard, so a hull faster than the ceiling allows never grows the ledger.
    const cut = tr.mapS - fig.window_map_sec;
    let drop = 0;
    while (drop < tr.pts.length && tr.pts[drop].mapS < cut) drop++;
    if (drop > 0) tr.pts.splice(0, drop);
    const over = tr.pts.length - fig.max_points;
    if (over > 0) tr.pts.splice(0, over);
  }
}

export const shipTracks = new ShipTracksPiece();
